import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Offerings } from '@revenuecat/purchases-js';
import type { WebEvent } from '../types/events';
import {
  WEB_ACCOUNT_STORAGE,
  WEB_EVENT_PACKAGES,
  WEB_EVENT_UPGRADES,
  WEB_GUEST_VIDEO_PASSES,
  WEB_HOST_VIDEO_ADDONS,
  type WebPurchaseCatalogItem,
} from '../domain/webPricingCatalog';
import {
  isRevenueCatWebConfigured,
  loadAccountStorageSummary,
  loadEventUpgradeSummary,
  loadWebPurchaseOfferings,
  localizedWebPrice,
  purchaseWebCatalogItem,
  resolveRevenueCatPackage,
  type WebAccountStorageSummary,
  type WebEventUpgradeSummary,
} from '../services/purchases';
import { formatManagementStorage } from '../services/eventManagement';
import './purchases.css';

type Props = {
  event: WebEvent;
  currentUserId: string;
  email?: string | null;
};

function statusLabel(storage: WebAccountStorageSummary | null): string {
  if (!storage?.active_status) return 'No active web storage subscription';
  return storage.active_status.replaceAll('_', ' ');
}

function PurchaseCard({
  item,
  offerings,
  current,
  disabled,
  onPurchase,
}: {
  item: WebPurchaseCatalogItem;
  offerings: Offerings | null;
  current?: boolean;
  disabled: boolean;
  onPurchase: (item: WebPurchaseCatalogItem) => void;
}) {
  const rcPackage = resolveRevenueCatPackage(offerings, item);
  const available = Boolean(rcPackage);
  const actualProductId = rcPackage?.webBillingProduct?.identifier ?? null;
  const mappingMatches = actualProductId === null || actualProductId === item.productId;
  const canBuy = available && mappingMatches && !disabled && !current;

  return (
    <article className={`purchase-option ${item.popular ? 'popular' : ''} ${current ? 'current' : ''}`}>
      <div className="purchase-option-topline">
        <span>{item.popular ? 'Most popular' : current ? 'Current' : item.kind.replaceAll('_', ' ')}</span>
        {!available && <em>Web setup needed</em>}
        {available && !mappingMatches && <em>Product mismatch</em>}
      </div>
      <h4>{item.displayName}</h4>
      <strong className="purchase-price">{localizedWebPrice(offerings, item)}</strong>
      <p>{item.description}</p>
      <button
        className="legacy-btn primary full"
        type="button"
        disabled={!canBuy}
        onClick={() => onPurchase(item)}
      >
        {current ? 'Current plan' : available ? 'Continue to secure checkout' : 'Not configured for web'}
      </button>
    </article>
  );
}

export function PurchasePanel({ event, currentUserId, email }: Props) {
  const [offerings, setOfferings] = useState<Offerings | null>(null);
  const [upgrade, setUpgrade] = useState<WebEventUpgradeSummary | null>(null);
  const [storage, setStorage] = useState<WebAccountStorageSummary | null>(null);
  const [configured, setConfigured] = useState(isRevenueCatWebConfigured());
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const isOrganizer = event.organizer_id === currentUserId;
  const currentSort = upgrade?.current_package_sort_order ?? 0;
  const currentPackageKey = upgrade?.current_package_key ?? 'free_event';

  const eventOptions = useMemo(() => {
    if (!isOrganizer) return [];
    const source = currentSort === 0 ? WEB_EVENT_PACKAGES : WEB_EVENT_UPGRADES;
    return source.filter((item) => item.sortOrder > currentSort);
  }, [currentSort, isOrganizer]);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [offeringState, nextUpgrade, nextStorage] = await Promise.all([
        loadWebPurchaseOfferings(currentUserId),
        loadEventUpgradeSummary(event.id).catch(() => null),
        isOrganizer ? loadAccountStorageSummary(currentUserId).catch(() => null) : Promise.resolve(null),
      ]);
      setConfigured(offeringState.configured);
      setOfferings(offeringState.offerings);
      setUpgrade(nextUpgrade);
      setStorage(nextStorage);
      if (offeringState.error) setError(offeringState.error);
    } finally {
      setLoading(false);
    }
  }, [currentUserId, event.id, isOrganizer]);

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [refresh]);

  async function buy(item: WebPurchaseCatalogItem) {
    setBusyKey(item.key);
    setError(null);
    setNotice(null);
    try {
      const outcome = await purchaseWebCatalogItem({
        item,
        event,
        userId: currentUserId,
        email,
        offerings,
      });
      if (outcome.cancelled) {
        setNotice('Purchase cancelled. No charge was completed.');
        return;
      }
      setNotice(outcome.syncMessage);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The purchase could not be completed.');
    } finally {
      setBusyKey(null);
    }
  }

  if (loading) return <div className="purchase-loading">Loading purchase options…</div>;

  return (
    <section className="purchase-center" aria-live="polite">
      <div className="purchase-heading">
        <div>
          <p className="eyebrow">Purchases</p>
          <h2>{isOrganizer ? 'Event packages, video & storage' : 'Add video access'}</h2>
          <p>
            {isOrganizer
              ? 'Hosts can upgrade the event, add host video capacity, and manage account storage.'
              : 'Guests can purchase video passes for their own uploads in this event. Storage and host package purchases are host-only.'}
          </p>
        </div>
        <button className="legacy-btn secondary" type="button" onClick={() => void refresh()} disabled={busyKey !== null}>Refresh</button>
      </div>

      {!configured && (
        <div className="purchase-setup-card">
          <strong>RevenueCat Web Billing setup is required before staging can charge a card.</strong>
          <p>The UI and secure purchase integration are installed. Add a RevenueCat Billing web config to the existing Gathrd RevenueCat project, attach the matching web products to the existing offerings/packages, then set the public web billing key as <code>VITE_REVENUECAT_WEB_API_KEY</code>.</p>
          <p>No RevenueCat secret key belongs in the browser.</p>
        </div>
      )}

      {error && <p className="error-banner">{error}</p>}
      {notice && <p className="purchase-notice">{notice}</p>}
      {busyKey && <p className="purchase-syncing">Secure checkout or entitlement synchronization is in progress…</p>}

      <div className="purchase-summary-grid">
        <article><span>Event package</span><strong>{upgrade?.current_package_display_name || 'Free Event'}</strong><small>{upgrade ? `${upgrade.current_guest_count}/${upgrade.current_guest_limit} guests` : 'Current event access'}</small></article>
        <article><span>Photo usage</span><strong>{upgrade ? `${upgrade.current_photo_count.toLocaleString()} / ${upgrade.current_photo_limit.toLocaleString()}` : '—'}</strong><small>Published event photos</small></article>
        <article><span>Host videos</span><strong>{upgrade ? `${upgrade.current_host_video_count} / ${upgrade.effective_host_video_limit}` : '—'}</strong><small>{upgrade?.addon_host_video_limit ? `Includes +${upgrade.addon_host_video_limit} add-on capacity` : 'Current capacity'}</small></article>
        {isOrganizer && <article><span>Account storage</span><strong>{storage ? formatManagementStorage(storage.total_storage_bytes) : '—'}</strong><small>{storage?.required_display_name || 'Storage usage'}</small></article>}
      </div>

      {isOrganizer && (
        <section className="purchase-section">
          <div className="purchase-section-heading">
            <div><h3>{currentSort === 0 ? 'Choose an event package' : 'Upgrade this event'}</h3><p>Paid event packages are one-time purchases for this event. Only higher tiers are offered after an event is upgraded.</p></div>
          </div>
          {eventOptions.length === 0 ? (
            <p className="purchase-empty">{currentPackageKey === 'premier_event' ? 'This event is already on the highest package.' : 'No higher event package is currently available.'}</p>
          ) : (
            <div className="purchase-options-grid">
              {eventOptions.map((item) => <PurchaseCard key={`${item.offeringId}:${item.key}`} item={item} offerings={offerings} disabled={busyKey !== null} onPurchase={(next) => void buy(next)} />)}
            </div>
          )}
        </section>
      )}

      {isOrganizer && (
        <section className="purchase-section">
          <div className="purchase-section-heading"><div><h3>Host video add-ons</h3><p>Increase the host’s video-upload capacity without changing the event package.</p></div></div>
          <div className="purchase-options-grid compact">
            {WEB_HOST_VIDEO_ADDONS.map((item) => <PurchaseCard key={item.key} item={item} offerings={offerings} disabled={busyKey !== null} onPurchase={(next) => void buy(next)} />)}
          </div>
        </section>
      )}

      {!isOrganizer && (
        <section className="purchase-section">
          <div className="purchase-section-heading"><div><h3>Guest video passes</h3><p>Video passes apply only to your uploads in this event. Guests cannot purchase event storage.</p></div></div>
          <div className="purchase-options-grid compact">
            {WEB_GUEST_VIDEO_PASSES.map((item) => <PurchaseCard key={item.key} item={item} offerings={offerings} disabled={busyKey !== null} onPurchase={(next) => void buy(next)} />)}
          </div>
        </section>
      )}

      {isOrganizer && (
        <section className="purchase-section">
          <div className="purchase-section-heading">
            <div>
              <h3>Account storage</h3>
              <p>Storage is a monthly host-account subscription across Gathrd events. RevenueCat Billing manages recurring charges and subscription lifecycle.</p>
            </div>
            <span className={`purchase-status-pill ${storage?.is_billing_issue ? 'issue' : storage?.is_subscription_active ? 'active' : ''}`}>{statusLabel(storage)}</span>
          </div>

          {storage?.is_subscription_active ? (
            <div className="purchase-active-subscription">
              <div><strong>{storage.active_tier_key?.replaceAll('_', ' ') || 'Storage subscription'}</strong><span>{storage.current_period_end ? `Current period through ${new Date(storage.current_period_end).toLocaleDateString()}` : 'Active RevenueCat storage subscription'}</span></div>
              <p>To prevent duplicate recurring subscriptions, Gathrd does not start another storage checkout while one is active. RevenueCat’s customer portal is the supported place to change plans, cancel, update payment details, and view invoices.</p>
            </div>
          ) : (
            <div className="purchase-options-grid">
              {WEB_ACCOUNT_STORAGE.map((item) => <PurchaseCard key={item.key} item={item} offerings={offerings} disabled={busyKey !== null} onPurchase={(next) => void buy(next)} />)}
            </div>
          )}
        </section>
      )}

      <p className="purchase-security-note">Gathrd does not unlock paid capacity from a browser success screen. The entitlement becomes active only after the verified RevenueCat webhook updates Supabase.</p>
    </section>
  );
}
